import { buildCopilotHeaders } from './copilotSession';

// ---------------------------------------------------------------------------
// Files Copilot made for the user (PDF reports, Excel/Word/CSV exports).
//
// An answer links them as `[name](/API/v2/copilot/files/<file id>)`. The id is a
// plain UUID that never expires; the API checks on every request who may read
// it. The link needs the bearer token, so a plain <a href> cannot open it:
// preview and download both go through fetch -> Blob.
//
// Report links of the previous Copilot (`/dl/<case id>/<uuid>` or a presigned `.../reports/<case id>/<uuid>.pdf`)
// are read here too and fetched from `/API/v2/copilot/reports/<case id>/<uuid>`: they are still in older chats.
// ---------------------------------------------------------------------------

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const FILES_PATH = '/API/v2/copilot/files';
const REPORTS_PATH = '/API/v2/copilot/reports';

// A link of copilot-min (`/copilot/files/<id>`) or of the previous Copilot (`/dl|reports/<case id>/<id>[.pdf]`).
const NEW_LINK = `/copilot/files/${UUID}`;
const LEGACY_LINK = `/(?:dl|reports)/\\d+/${UUID}(?:\\.pdf)?`;
const NEW_LINK_RE = new RegExp(`/copilot/files/(${UUID})(?:[?#]|$)`, 'i');
const LEGACY_LINK_RE = new RegExp(`/(?:dl|reports)/(\\d+)/(${UUID})(?:\\.pdf)?(?:[?#]|$)`, 'i');

// Tolerates an absolute URL and a query/hash after the id.
export const FILE_LINK_RE = new RegExp(`(?:${NEW_LINK}|${LEGACY_LINK})(?:[?#]|$)`, 'i');
// `[label](link)` with a file link, global, to walk an answer's links.
const MARKDOWN_FILE_LINK_RE = new RegExp(
  `\\[([^\\]\\n]*)\\]\\(([^)\\s]*(?:${NEW_LINK}|${LEGACY_LINK})[^)\\s]*)\\)`,
  'gi',
);
// A file link written as a bare URL or path (no markdown).
const BARE_FILE_LINK_RE = new RegExp(
  `(?<![\\](])(?:(?:https?://[^\\s)]*)?/API/v2/copilot/files/${UUID}|https?://[^\\s)]*${LEGACY_LINK}[^\\s)]*)`,
  'gi',
);
// A link wrapped in backticks, which markdown never turns into a link.
const CODE_WRAPPED_FILE_LINK_RE = new RegExp(`\`([^\`\\n]*(?:${NEW_LINK}|${LEGACY_LINK})[^\`\\n]*)\``, 'gi');

/** `{fileId}` from a copilot-min file link, `{fileId, narrativeId}` from a previous-Copilot report link, or null. */
export function parseFileRef(url) {
  if (!url) return null;
  const file = NEW_LINK_RE.exec(url);
  if (file) return { fileId: file[1].toLowerCase() };
  const report = LEGACY_LINK_RE.exec(url);
  return report ? { fileId: report[2].toLowerCase(), narrativeId: Number(report[1]) } : null;
}

export function isSameFileRef(a, b) {
  return Boolean(a && b) && a.fileId === b.fileId;
}

export function isPdfName(name) {
  return /\.pdf$/i.test(name ?? '');
}

/** The newest PDF linked in an answer as `{fileId, name}`, or null: a revised report supersedes the earlier one. */
export function findLastPdfRef(text) {
  let last = null;
  for (const match of (text ?? '').matchAll(MARKDOWN_FILE_LINK_RE)) {
    const ref = parseFileRef(match[2]);
    // A previous-Copilot link is always a report, whatever its label says.
    if (ref && (isPdfName(match[1]) || ref.narrativeId != null)) last = { ...ref, name: match[1] };
  }
  return last;
}

// Any markdown link or autolink, whatever its target: a bare URL inside one is already a link and must stay as it is.
const ANY_MARKDOWN_LINK_RE = /\[[^\]\n]*\]\([^)\n]*\)|<[^>\s]+>/g;

function wrapBareLink(url, offset, whole) {
  for (const link of whole.matchAll(ANY_MARKDOWN_LINK_RE)) {
    if (offset >= link.index && offset < link.index + link[0].length) return url;
  }
  return `[${url}](${url})`;
}

/**
 * Makes an answer's file links render as cards: unwraps backticked links, turns bare ones into markdown links and
 * drops a repeat of a link already shown (the model sometimes writes the link and the system adds it again).
 */
export function normalizeFileLinks(text) {
  const seen = new Set();
  return text
    .replace(CODE_WRAPPED_FILE_LINK_RE, '$1')
    .replace(BARE_FILE_LINK_RE, wrapBareLink)
    .replace(MARKDOWN_FILE_LINK_RE, (whole, _label, url) => {
      const id = parseFileRef(url)?.fileId;
      if (!id) return whole;
      if (seen.has(id)) return '';
      seen.add(id);
      return whole;
    });
}

// While an answer streams, a link may be half received (`[report.pdf](/API/v2/cop`). Without this the raw
// fragment flashes in the text; it is shown as a pending card instead (LinkRenderer maps #file-loading to one).
const PARTIAL_FILE_LINK_RE = /\[[^\]\n]*\]\(((?:https?:)?\/[^)\s]*)$/;
const FILES_URL_PREFIX = `${FILES_PATH}/`;

export function maskPartialFileLink(text) {
  const match = PARTIAL_FILE_LINK_RE.exec(text);
  if (!match) return text;
  const target = match[1];
  const isFilesLink =
    FILES_URL_PREFIX.startsWith(target) || target.includes('/copilot/files/') || /\/(?:dl|reports)\//.test(target);
  return isFilesLink ? text.slice(0, match.index) + '[…](#file-loading)' : text;
}

export function buildFileApiUrl({ fileId, narrativeId }) {
  return narrativeId != null ? `${REPORTS_PATH}/${narrativeId}/${fileId}` : `${FILES_PATH}/${fileId}`;
}

export class FileFetchError extends Error {
  constructor(status) {
    super(`Copilot file request failed: ${status}`);
    this.name = 'FileFetchError';
    this.status = status;
  }
}

// The API names the file in Content-Disposition; non-Latin names come through
// only in the RFC 5987 `filename*` form, the plain one is underscores.
function fileNameFromDisposition(header) {
  if (!header) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : null;
}

const FILE_TIMEOUT_MS = 120_000;

// Same deadline API.ts gives every request; this fetch goes around API.ts so
// the Blob can be read, so a stalled download would otherwise hang the
// preview/download button forever. The caller's signal (panel unmount) still
// aborts early.
function withTimeout(signal) {
  const timeoutSignal = AbortSignal.timeout(FILE_TIMEOUT_MS);
  if (!signal) return timeoutSignal;
  const combined = new AbortController();
  for (const source of [signal, timeoutSignal]) {
    if (source.aborted) {
      combined.abort(source.reason);
      break;
    }
    source.addEventListener('abort', () => combined.abort(source.reason), { once: true });
  }
  return combined.signal;
}

export async function fetchCopilotFile(ref, { signal } = {}) {
  const response = await fetch(buildFileApiUrl(ref), {
    method: 'GET',
    headers: buildCopilotHeaders(),
    signal: withTimeout(signal),
  });
  if (!response.ok) throw new FileFetchError(response.status);
  const blob = await response.blob();
  return {
    blob,
    fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')) || ref.name || `copilot-file-${ref.fileId}`,
  };
}

// 404 covers both "no such file" and "no access" (the API deliberately doesn't
// distinguish); anything else is the service being unavailable, so suggest
// trying again rather than giving up.
export function fileErrorMessage(error, t) {
  if (error?.status === 404) return t("File not found or you don't have access to it.");
  return t('The file is temporarily unavailable. Please try again later.');
}

export function saveBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a tick to start the download before the URL goes away.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
